import { useState, useEffect, useRef, useCallback } from 'react';
import { useFlow } from '../contexts/FlowContext';

const TERMINAL = new Set(['SYNCED', 'FAILED']);

const toStatus = (flow) => ({
  syncStatus: flow.syncStatus,
  validationStatus: flow.validationStatus,
  validationErrors: flow.validationErrors,
  syncError: flow.syncError,
});

/**
 * Tracks the background sync + validation state of a saved flow (ADR-0006).
 *
 * Saving is local-first: the backend persists the flow instantly as PENDING and a worker
 * pushes it to Meta and resolves the strict validation verdict. Call `track(savedFlow)` after
 * a save to poll until the flow reaches a terminal state (SYNCED / FAILED), exposing the latest
 * status so the builder can show "Saving… / Synced ✓ / Validation issues".
 *
 * Both builders use this at once (they stay mounted side by side), and each used to keep its own
 * copy of the status and run its own poller, so the assistant and the canvas could show different
 * states for the same flow. The status now comes from the shared `currentFlow`, which every poll
 * writes to (FlowContext.refreshFlow), so the two views cannot disagree; only an `active` view
 * polls.
 *
 * @param {number} pollIntervalMs Poll cadence while the flow is still syncing
 * @param {{active?: boolean}} options `active` false for the builder that is not on screen
 */
export function useFlowSyncStatus(pollIntervalMs = 2000, { active = true } = {}) {
  const { refreshFlow, currentFlow } = useFlow();
  const [tracked, setTracked] = useState(null);
  const timerRef = useRef(null);
  const flowIdRef = useRef(null);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    const fresh = await refreshFlow(flowIdRef.current);
    // Ignore results for a flow we are no longer tracking (rapid re-saves / navigation).
    if (!fresh || fresh.id !== flowIdRef.current) {
      return;
    }
    setTracked(toStatus(fresh));
    if (!TERMINAL.has(fresh.syncStatus)) {
      timerRef.current = setTimeout(poll, pollIntervalMs);
    }
  }, [refreshFlow, pollIntervalMs]);

  const track = useCallback((flow) => {
    stop();
    flowIdRef.current = flow?.id ?? null;
    setTracked(flow ? toStatus(flow) : null);
  }, [stop]);

  // Poll while this view is on screen and the tracked flow is still on its way to Meta.
  const trackedId = flowIdRef.current;
  const trackedSync = tracked?.syncStatus;
  useEffect(() => {
    if (!active || !trackedId || !trackedSync || TERMINAL.has(trackedSync)) {
      stop();
      return undefined;
    }
    timerRef.current = setTimeout(poll, pollIntervalMs);
    return stop;
  }, [active, trackedId, trackedSync, poll, pollIntervalMs, stop]);

  useEffect(() => stop, [stop]);

  const shared = tracked && currentFlow?.id && currentFlow.id === trackedId && currentFlow.syncStatus
    ? toStatus(currentFlow)
    : null;
  return { status: shared || tracked, track, stop };
}
